import type { NormalizeOAS, OASOutput, OASRequestParams } from 'fets';
import type { ApiClient } from '../fetsClient';
import type openapi from '../openapi';

type O = NormalizeOAS<typeof openapi>;
type RequestParams = OASRequestParams<O, '/api/scenes', 'post'>;
type ResponseBody = OASOutput<O, '/api/scenes', 'post'>;

type Params = {
  panoramaSceneId: number;
};

type TourScenePanorama = {
  guid: string;
  baseDir: string;
  urlSuffix: string;
  startingSceneIndex: number;
  xmlDefinition: string;
};

type TourSceneLinkPosition = {
  xAxis: number;
  yAxis: number;
  sceneIndex: number;
};

type Icon = {
  identifier: string | null;
  scale: number;
};

type TourSceneLink = {
  guid: string;
  tourSceneOriginGuid: string;
  tourSceneDestinationGuid: string;
  position: TourSceneLinkPosition;
  icon: Icon;
  destinationLabel: string | null;
  hasTilt: boolean;
};

type TourScene = {
  name: string;
  guid: string;
  hasProcessingFinishedSuccessfully: boolean | null;
  thumbnailUrl: string | null;
  tourScenePanorama: TourScenePanorama | null;
  links: TourSceneLink[];
};

type Result = TourScene[];

export const prepareSceneFactory = (apiClient: ApiClient) => {
  const prepareScene = async (variables: Params) => {
    const requestBody = mapInputToRequestBody(variables);
    const response = await apiClient['/api/scenes'].post(requestBody);
    const responseBody = await response.json();

    return mapResponseToResult(responseBody);
  };

  const mapInputToRequestBody = (input: Params): RequestParams => {
    return {
      query: {
        PanoramaSceneId: input.panoramaSceneId,
      },
    };
  };

  const mapResponseToResult = (response: ResponseBody): Result => {
    return response.map((scene) => ({
      name: scene.name,
      guid: scene.guid,
      hasProcessingFinishedSuccessfully: scene.hasProcessingFinishedSuccessfully ?? null,
      thumbnailUrl: scene.thumbnailUrl ?? null,
      tourScenePanorama: scene.tourScenePanorama
        ? {
            guid: scene.tourScenePanorama.guid,
            baseDir: scene.tourScenePanorama.baseDir,
            urlSuffix: scene.tourScenePanorama.urlSuffix,
            startingSceneIndex: scene.tourScenePanorama.startingSceneIndex,
            xmlDefinition: scene.tourScenePanorama.xmlDefinition,
          }
        : null,
      links: scene.links.map((link) => ({
        guid: link.guid,
        tourSceneOriginGuid: link.tourSceneOriginGuid,
        tourSceneDestinationGuid: link.tourSceneDestinationGuid,
        position: {
          xAxis: link.position.xAxis,
          yAxis: link.position.yAxis,
          sceneIndex: link.position.sceneIndex,
        },
        icon: {
          identifier: link.icon.identifier ?? null,
          scale: link.icon.scale,
        },
        destinationLabel: link.destinationLabel ?? null,
        hasTilt: link.hasTilt,
      })),
    }));
  };

  return prepareScene;
};
